import { isPlatformBrowser } from '@angular/common';
import { Injectable, signal, inject, PLATFORM_ID, OnDestroy } from '@angular/core';

/**
 * NetworkService manages the online/offline state of the application.
 * It wraps the native browser navigator.onLine property and network events
 * into a reactive Angular Signal.
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // Internal writable signal. Defaults to true for SSR or missing navigator.
  private readonly _isOnline = signal<boolean>(true);

  /**
   * Reactive signal representing the current network connection status.
   * True if online, false if offline.
   */
  public readonly isOnline = this._isOnline.asReadonly();

  private readonly onlineHandler = (): void => this._isOnline.set(true);
  private readonly offlineHandler = (): void => this._isOnline.set(false);

  constructor() {
    if (this.isBrowser) {
      // Set initial state based on the browser's navigator API
      this._isOnline.set(navigator.onLine);

      // Listen for network status changes
      window.addEventListener('online', this.onlineHandler);
      window.addEventListener('offline', this.offlineHandler);
    }
  }

  /**
   * Force-marks the connection as offline when the API is unreachable despite
   * navigator.onLine being true (e.g. WiFi with no actual internet access).
   * Resets automatically when the browser fires the 'online' event.
   */
  setOffline(): void {
    this._isOnline.set(false);
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      window.removeEventListener('online', this.onlineHandler);
      window.removeEventListener('offline', this.offlineHandler);
    }
  }
}
